import React, { useState } from 'react';
import {
  Car, AlertCircle, Mail, Lock, User, Building2, Phone, Gift,
  Eye, EyeOff, Loader2, ArrowRight, CheckCircle2,
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { supabase } from '../../../config/supabase';

/**
 * Reusable, icon-prefixed input with focus/hover micro-interactions.
 * Purely presentational — keeps the form markup clean and consistent.
 */
function LoginInput({ icon: Icon, rightSlot, className = '', ...props }) {
  return (
    <div className="group relative">
      <Icon
        className="pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400 transition-colors duration-200 group-focus-within:text-indigo-500"
      />
      <input
        {...props}
        className={
          'h-12 w-full rounded-xl border border-slate-200 bg-slate-50/80 pl-11 pr-4 text-[15px] text-slate-900 outline-none transition-all duration-200 ' +
          'placeholder:text-slate-400 hover:border-slate-300 focus:border-indigo-400 focus:bg-white focus:ring-4 focus:ring-indigo-500/10 ' +
          className
        }
      />
      {rightSlot}
    </div>
  );
}

export default function LoginPage({ settings = {} }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [agencyName, setAgencyName] = useState('');
  const [phone, setPhone] = useState('');
  // Referral code (pre-filled from ?ref= link, editable by the user).
  const [refCode, setRefCode] = useState(() => {
    try {
      return (new URLSearchParams(window.location.search).get('ref')
        || sessionStorage.getItem('referralCode') || '').trim();
    } catch (_) { return ''; }
  });
  // Persist an incoming ?ref= referral code and pre-open signup for referred users.
  const [isSignup, setIsSignup] = useState(() => {
    try {
      const ref = new URLSearchParams(window.location.search).get('ref');
      if (ref) { sessionStorage.setItem('referralCode', ref); return true; }
    } catch (_) {}
    return false;
  });
  const [error, setError] = useState('');
  const [registered, setRegistered] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { loginWithMasterPassword, loginWithStaff } = useAuth();

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') submit();
  };

  const submit = async () => {
    setError('');
    setLoading(true);

    try {
      // 1. MASTER PASSWORD CHECK (server-side verify)
      let masterOk = false;
      let masterToken = '';
      if (password) {
        try {
          const r = await fetch('/api/verify-password', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ password })
          });
          masterOk = r.ok;
          if (r.ok) {
            const body = await r.json().catch(() => ({}));
            masterToken = body.token || '';
          }
        } catch (_) {
          masterOk = false;
        }
      }

      if (masterOk) {
        const userObj = {
          email: email || 'master@raparkour.access',
          uid: 'master-' + (email || 'admin').replace(/[^a-z0-9]/gi, '_'),
          displayName: email || 'Master Admin',
          isMaster: true,
          token: masterToken   // signed master token for authenticated backend calls
        };
        loginWithMasterPassword(email, userObj);
        setLoading(false);
        return;
      }

      // 2. SUPABASE MODE
      if (isSignup) {
        if (!email.trim() || !password.trim()) {
          setError('Email et mot de passe obligatoires.');
          setLoading(false);
          return;
        }
        if (!name.trim()) {
          setError('Nom complet obligatoire.');
          setLoading(false);
          return;
        }

        const { data: signUpData, error: signUpErr } = await supabase.auth.signUp({
          email: email.trim(),
          password
        });

        if (signUpErr) throw signUpErr;

        const uid = signUpData.user?.id;
        await supabase.auth.signOut();

        if (uid) {
          // Referral capture: from the entered/pre-filled code (short code or full uid).
          let referredBy = '';
          try {
            const ref = (refCode || '').trim();
            if (ref) {
              if (ref.length >= 36) {
                referredBy = ref; // legacy full-uid link
              } else {
                // Short code (uid prefix) → resolve to the full referrer uid.
                const { data } = await supabase.rpc('resolve_referral', { p_code: ref });
                referredBy = data || '';
              }
            }
          } catch (_) {}
          if (referredBy === uid) referredBy = '';

          const { error: upsertErr } = await supabase
            .from('user_registrations')
            .upsert({
              id: uid,
              uid,
              agency_id: uid,
              status: 'pending',
              data: {
                uid,
                agencyId: uid,
                email: email.trim(),
                name: name.trim(),
                agencyName: agencyName.trim() || name.trim(),
                phone: phone.trim(),
                createdAt: new Date().toISOString(),
                ...(referredBy ? { referredBy, referralRewarded: false } : {})
              }
            });
          try { sessionStorage.removeItem('referralCode'); } catch (_) {}
          if (upsertErr) throw upsertErr;
        }
        setRegistered(true);
      } else {
        if (!email.trim() || !password.trim()) {
          setError('Email et mot de passe obligatoires.');
          setLoading(false);
          return;
        }

        // 2a. Employee (staff) login — username-based. Tried before the agency
        // (Supabase email) login; a non-matching username simply falls through.
        try {
          const sr = await fetch('/api/staff/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username: email.trim(), password }),
          });
          if (sr.ok) {
            const session = await sr.json().catch(() => null);
            if (session && session.ok && session.token) {
              loginWithStaff(session);
              setLoading(false);
              return;
            }
          }
        } catch (_) { /* not a staff account — continue to agency login */ }

        const { data: signInData, error: signInErr } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password
        });

        if (signInErr) throw signInErr;

        // Check registration status BEFORE allowing access
        const uid = signInData.user?.id;
        if (uid) {
          const { data: reg } = await supabase
            .from('user_registrations')
            .select('status, data')
            .eq('uid', uid)
            .maybeSingle();

          if (reg && reg.status === 'pending') {
            await supabase.auth.signOut();
            setError('⏳ Votre compte est en attente d\'approbation par l\'administrateur.');
            setLoading(false);
            return;
          }

          if (reg && reg.status === 'rejected') {
            await supabase.auth.signOut();
            // Distinguish an automatic subscription suspension from a manual refusal.
            if (reg.data?.suspendedReason === 'subscription_expired') {
              setError('🚫 Abonnement expiré : votre accès a été suspendu. Contactez l\'administrateur pour réactiver après renouvellement.');
            } else {
              setError('❌ Votre accès a été suspendu. Contactez l\'administrateur.');
            }
            setLoading(false);
            return;
          }
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const companyName = settings.companyName || 'CAR TRACK APP';

  // Soft gradient backdrop shared by both screens.
  const Backdrop = () => (
    <>
      <div className="pointer-events-none absolute -top-32 -left-24 h-96 w-96 rounded-full bg-indigo-300/30 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-24 h-[28rem] w-[28rem] rounded-full bg-sky-300/20 blur-3xl" />
    </>
  );

  const Brand = () => (
    <div className="mb-8 flex flex-col items-center text-center">
      {settings.logo ? (
        <img src={settings.logo} alt={companyName} className="mb-4 h-12 w-auto object-contain" />
      ) : (
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-600 shadow-lg shadow-indigo-600/25">
          <Car className="h-7 w-7 text-white" />
        </div>
      )}
      <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">{companyName}</h1>
    </div>
  );

  if (registered) {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-br from-slate-50 via-white to-indigo-50/50 p-4 sm:p-6">
        <Backdrop />
        <div className="relative w-full max-w-md rounded-3xl border border-white/60 bg-white/80 p-8 text-center shadow-[0_20px_60px_-15px_rgba(15,23,42,0.18)] backdrop-blur-xl sm:p-10">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50">
            <CheckCircle2 className="h-8 w-8 text-emerald-500" />
          </div>
          <h2 className="mb-2 text-2xl font-bold tracking-tight text-slate-900">Demande envoyée</h2>
          <p className="mx-auto mb-6 max-w-sm text-[15px] leading-relaxed text-slate-500">
            Votre compte a été créé. Un administrateur va examiner votre demande avant d'activer votre accès.
          </p>
          <p className="mb-6 text-sm text-slate-400">
            {email}
          </p>
          <button
            onClick={() => { setRegistered(false); setIsSignup(false); setPassword(''); setName(''); }}
            className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 font-semibold text-white shadow-lg shadow-indigo-600/25 transition-all duration-200 hover:bg-indigo-700 hover:shadow-xl active:scale-[0.99]"
          >
            Aller à la connexion
            <ArrowRight className="h-[18px] w-[18px]" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-br from-slate-50 via-white to-indigo-50/50 p-4 sm:p-6">
      <Backdrop />

      <div className="relative w-full max-w-md">
        <div className="rounded-3xl border border-white/60 bg-white/80 p-8 shadow-[0_20px_60px_-15px_rgba(15,23,42,0.18)] backdrop-blur-xl sm:p-10">
          <Brand />

          {/* Tab switcher */}
          <div className="mb-7 flex rounded-xl bg-slate-100 p-1">
            <button
              type="button"
              onClick={() => { setIsSignup(false); setError(''); }}
              className={`flex-1 rounded-lg py-2.5 text-sm font-semibold transition-all duration-200 ${
                !isSignup ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Connexion
            </button>
            <button
              type="button"
              onClick={() => { setIsSignup(true); setError(''); }}
              className={`flex-1 rounded-lg py-2.5 text-sm font-semibold transition-all duration-200 ${
                isSignup ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Créer un compte
            </button>
          </div>

          <div className="space-y-3.5">
            {/* Signup extra fields */}
            {isSignup && (
              <>
                <LoginInput
                  icon={User}
                  type="text"
                  placeholder="Nom complet *"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyPress={handleKeyPress}
                  autoFocus
                />
                <LoginInput
                  icon={Building2}
                  type="text"
                  placeholder="Nom de l'agence"
                  value={agencyName}
                  onChange={(e) => setAgencyName(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
                <LoginInput
                  icon={Phone}
                  type="tel"
                  placeholder="Téléphone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
                <div>
                  <LoginInput
                    icon={Gift}
                    type="text"
                    placeholder="Code de parrainage (optionnel)"
                    value={refCode}
                    onChange={(e) => setRefCode(e.target.value.trim())}
                    onKeyPress={handleKeyPress}
                  />
                  {refCode && (
                    <p className="mt-1.5 px-1 text-[12px] font-medium text-emerald-600">Code de parrainage appliqué</p>
                  )}
                </div>
                <div className="!mt-5 h-px bg-slate-100" />
              </>
            )}

            <LoginInput
              icon={Mail}
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={handleKeyPress}
              autoFocus={!isSignup}
            />

            {/* Password field */}
            <LoginInput
              icon={Lock}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Mot de passe"
              className="pr-11"
              rightSlot={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-600 focus:outline-none"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                >
                  {showPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                </button>
              }
            />

            {/* Error */}
            {error && (
              <div className="flex items-start gap-2.5 rounded-xl border border-red-100 bg-red-50 p-3.5 text-sm text-red-700">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" />
                <span className="leading-snug">{error}</span>
              </div>
            )}

            {/* Submit button */}
            <button
              type="button"
              onClick={submit}
              disabled={loading || !password || !email}
              className="mt-1 inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 text-[15px] font-semibold text-white shadow-lg shadow-indigo-600/25 transition-all duration-200 hover:bg-indigo-700 hover:shadow-xl hover:shadow-indigo-600/30 active:scale-[0.99] disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none"
            >
              {loading ? (
                <>
                  <Loader2 className="h-[18px] w-[18px] animate-spin" />
                  {isSignup ? 'Inscription…' : 'Connexion…'}
                </>
              ) : (
                <>
                  {isSignup ? 'Créer mon compte' : 'Se connecter'}
                  <ArrowRight className="h-[18px] w-[18px]" />
                </>
              )}
            </button>

            {/* Signup info */}
            {isSignup && (
              <p className="pt-1 text-center text-[12.5px] leading-relaxed text-slate-400">
                Votre demande sera examinée par un administrateur avant l'accès à l'application.
              </p>
            )}
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-slate-400">
          © {new Date().getFullYear()} {companyName}
        </p>
      </div>
    </div>
  );
}
